import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, BookOpen, Info, Plus, Minus } from 'lucide-react';
import axios from 'axios';
import { verseCountFor } from '../constants/bibleConstants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Self-hosted, distinct public-domain / openly-licensed translations only.
// `style` drives the "Modern" vs "Classic" column tag.
const TRANSLATION_META = {
  en: [
    { id: 'KJV', style: 'classic' },
    { id: 'ASV', style: 'classic' },
    { id: 'YLT', style: 'classic' },
    { id: 'DBY', style: 'classic' },
    { id: 'WEB', style: 'modern' },
    { id: 'BSB', style: 'modern' },
    { id: 'NET', style: 'modern' },
    { id: 'FBV', style: 'modern' },
    { id: 'BBE', style: 'modern' },
  ],
  es: [
    { id: 'RVR1909', style: 'classic' },
    { id: 'RVG', style: 'modern' },
    { id: 'ONBV', style: 'modern' },
    { id: 'ESS', style: 'modern' },
    { id: 'PDPT', style: 'modern' },
    { id: 'BLM', style: 'modern' },
    { id: 'VBL', style: 'modern' },
    { id: 'RV1865', style: 'classic' },
    { id: 'TA', style: 'classic' },
    { id: 'RV1569', style: 'classic' },
    { id: 'RV1602', style: 'classic' },
    { id: 'JUN', style: 'classic' },
    { id: 'BLL', style: 'modern' },
  ],
};

const DEFAULTS = {
  en: ['KJV', 'WEB', 'BSB', 'NET'],
  es: ['RVR1909', 'RVG', 'PDPT', 'BLM'],
};

const MIN_SELECTED = 2;
const MAX_SELECTED = 6;
const MAX_RANGE = 5; // how many verses can be compared at once (anchor + up to 4)

// Parse "Book Chapter:Verse" (handles multi-word books like "Song of Solomon").
const parseRef = (ref) => {
  const m = /^(.+)\s+(\d+):(\d+)/.exec(ref || '');
  return m ? { book: m[1].trim(), chapter: parseInt(m[2], 10), verse: parseInt(m[3], 10) } : null;
};

/**
 * QuickCompareDialog - Fast side-by-side verse comparison.
 * Users pick which public-domain translations to compare (persisted per
 * language). Each column is tagged Modern/Classic, and a note explains why
 * copyrighted versions (NIV/ESV/…) aren't available.
 */
const QuickCompareDialog = ({ isOpen, onClose, verse }) => {
  const { language } = useLanguage();
  const es = language === 'es';
  const langKey = es ? 'es' : 'en';
  const meta = TRANSLATION_META[langKey];
  const styleOf = (id) => meta.find((m) => m.id === id)?.style;

  const storageKey = `lumera_compare_translations_${langKey}`;
  const [selected, setSelected] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (Array.isArray(raw) && raw.length >= MIN_SELECTED) {
        return raw.filter((id) => meta.some((m) => m.id === id));
      }
    } catch { /* ignore */ }
    return DEFAULTS[langKey];
  });
  const [rows, setRows] = useState([]); // [{ reference, cells: [{translation,text,error}] }]
  const [loading, setLoading] = useState(false);
  const [extra, setExtra] = useState(0); // additional verses beyond the anchor
  const anchor = parseRef(verse?.reference);
  const maxExtra = anchor
    ? Math.max(0, Math.min(MAX_RANGE - 1, verseCountFor(anchor.book, anchor.chapter) - anchor.verse))
    : 0;

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(selected));
  }, [selected, storageKey]);

  useEffect(() => {
    if (isOpen && verse) loadComparisons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, verse, selected, extra]);

  // New anchor verse → reset the range back to a single verse.
  useEffect(() => { setExtra(0); }, [verse?.reference]);

  const toggle = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) {
        if (prev.length <= MIN_SELECTED) return prev; // keep at least 2
        return prev.filter((t) => t !== id);
      }
      if (prev.length >= MAX_SELECTED) return prev; // cap
      // preserve catalog order for a stable grid
      return meta.filter((m) => prev.includes(m.id) || m.id === id).map((m) => m.id);
    });
  };

  const loadComparisons = async () => {
    if (!anchor) return;
    setLoading(true);
    try {
      // Build the verse range (anchor .. anchor+extra, capped to the chapter).
      const refs = [];
      for (let i = 0; i <= extra; i++) {
        refs.push({ n: anchor.verse + i, reference: `${anchor.book} ${anchor.chapter}:${anchor.verse + i}` });
      }
      const nextRows = await Promise.all(refs.map(async ({ reference }) => {
        const cells = await Promise.all(selected.map(async (translation) => {
          try {
            const response = await axios.get(`${BACKEND_URL}/api/bible/verse`, {
              params: { reference, translation },
              withCredentials: true,
            });
            return { translation, text: response.data.text || '', error: !!response.data.is_fallback };
          } catch (error) {
            return { translation, text: '', error: true };
          }
        }));
        return { reference, cells };
      }));
      setRows(nextRows);
    } catch (error) {
      console.error('Error loading comparisons:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!verse) return null;

  const StyleTag = ({ style }) => {
    if (!style) return null;
    const modern = style === 'modern';
    return (
      <span
        className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
          modern
            ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
            : 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
        }`}
        data-testid={`compare-style-tag-${style}`}
      >
        {modern ? (es ? 'Moderna' : 'Modern') : (es ? 'Clásica' : 'Classic')}
      </span>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="quick-compare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            {es ? 'Comparación Rápida' : 'Quick Comparison'}
          </DialogTitle>
          <p className="text-sm text-slate-600 dark:text-slate-400 font-semibold" data-testid="compare-range-label">
            {extra > 0 && anchor ? `${anchor.book} ${anchor.chapter}:${anchor.verse}-${anchor.verse + extra}` : verse.reference}
          </p>
        </DialogHeader>

        {/* Version picker */}
        <div className="pt-1" data-testid="compare-version-picker">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
            {es ? 'Elige versiones' : 'Choose versions'}
          </p>
          <div className="flex flex-wrap gap-2">
            {meta.map((m) => {
              const active = selected.includes(m.id);
              return (
                <button
                  key={m.id}
                  onClick={() => toggle(m.id)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    active
                      ? 'bg-purple-600 border-purple-600 text-white'
                      : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-purple-400'
                  }`}
                  data-testid={`compare-toggle-${m.id}`}
                  aria-pressed={active}
                >
                  {m.id}
                  <span className={`text-[9px] uppercase ${active ? 'text-white/80' : 'text-slate-400'}`}>
                    {m.style === 'modern' ? (es ? 'mod' : 'mod') : (es ? 'clás' : 'clas')}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-400 mt-1.5">
            {es ? `Selecciona de ${MIN_SELECTED} a ${MAX_SELECTED} versiones.` : `Pick ${MIN_SELECTED} to ${MAX_SELECTED} versions.`}
          </p>
        </div>

        {/* Verse range: compare more than one verse at a time */}
        <div className="flex items-center justify-between gap-2 pt-1" data-testid="compare-range-picker">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {es ? 'Versículos' : 'Verses'}: {extra + 1}
          </p>
          <div className="flex items-center gap-1.5">
            <Button
              size="sm" variant="outline" className="h-7 px-2"
              onClick={() => setExtra((e) => Math.max(0, e - 1))}
              disabled={extra <= 0}
              data-testid="compare-remove-verse-btn"
            >
              <Minus className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="sm" variant="outline" className="h-7 px-2"
              onClick={() => setExtra((e) => Math.min(maxExtra, e + 1))}
              disabled={extra >= maxExtra}
              data-testid="compare-add-verse-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> {es ? 'Añadir versículo' : 'Add verse'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            <span className="ml-3 text-slate-600 dark:text-slate-400">
              {es ? 'Cargando traducciones...' : 'Loading translations...'}
            </span>
          </div>
        ) : (
          <div className="space-y-4 py-2" data-testid="compare-results-grid">
            {rows.map((row) => (
              <div key={row.reference} data-testid={`compare-verse-group-${row.reference}`}>
                {rows.length > 1 && (
                  <p className="text-xs font-bold text-purple-600 dark:text-purple-400 mb-2">{row.reference}</p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {row.cells.map((trans) => (
                    <div
                      key={trans.translation}
                      className="p-4 rounded-lg border-2 border-slate-200 dark:border-slate-700 hover:border-purple-400 dark:hover:border-purple-600 transition-colors"
                      data-testid={`compare-card-${trans.translation}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="font-bold">{trans.translation}</Badge>
                          <StyleTag style={styleOf(trans.translation)} />
                        </div>
                        {trans.error && (
                          <span className="text-xs text-amber-600 dark:text-amber-400">
                            {es ? 'Usando texto original' : 'Using original text'}
                          </span>
                        )}
                      </div>
                      <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">{trans.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Copyright / licensing note */}
        <div
          className="flex items-start gap-2 rounded-lg bg-slate-100 dark:bg-slate-800/60 p-3 text-[12px] leading-relaxed text-slate-500 dark:text-slate-400"
          data-testid="compare-copyright-note"
        >
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <p>
            {es
              ? 'Solo mostramos traducciones de dominio público o con licencia abierta. Versiones con derechos de autor como NVI, RVR1960, LBLA y NTV requieren una licencia de pago y aún no están disponibles.'
              : "Only public-domain and openly-licensed translations are shown here. Copyrighted versions like NIV, ESV, NKJV and NLT require paid licensing and aren't available yet."}
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button onClick={onClose} variant="outline" data-testid="compare-close-btn">
            {es ? 'Cerrar' : 'Close'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuickCompareDialog;
